import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence } from "framer-motion";
import styled from "styled-components";

import {
  FullWidthRow,
  ResponsiveTable,
  Table,
  TableContent,
  Th,
} from "@/components/Table";
import Text from "@/components/Text";
import { SrOnly } from "@/components/Layout/Parts";
import TestingChannelsTableRow from "./TestingChannelsTableRow";
import { TestingChannelResource } from "@/types/resources";
import { api } from "@/utils/api";
import AddButton from "@/components/AddButton";
import TestingChannelFormModal from "./TestingChannelFormModal";
import ToggableCardHeading, { Box } from "./ToggableCardHeading";

const StyledTable = styled(Table)`
  box-shadow: 0;
  th,
  td {
    padding: 16px 12px;
    font-size: 11px;
    vertical-align: middle;
    &:nth-child(1) {
      width: 35%;
      padding-left: 24px;
    }
    &:nth-child(2) {
      width: 20%;
    }
    &:last-child {
      width: 56px;
    }
  }
  th {
    vertical-align: top;
  }
`;

const StyledFullWidthRow = styled(FullWidthRow)`
  border-radius: 4px;
`;

const TestingChannelsTable = () => {
  const [showAddModal, setShowAddModal] = useState(false);

  const [showContent, setShowContent] = useState(false);

  const { data, isError, isLoading } = useQuery<TestingChannelResource[]>(
    ["testing_channels"],
    () => api.get<never, TestingChannelResource[]>(`/v1/testing-channels`),
    {
      staleTime: 120 * 1000,
    }
  );

  return (
    <Box>
      <ToggableCardHeading
        setShowModal={setShowAddModal}
        setShowContent={setShowContent}
        count={data?.length}
        showContent={showContent}
        title="Channels added"
        AddbuttonText="Add channel"
      />
      {showContent && (
        <ResponsiveTable>
          <StyledTable $fixed>
            <thead>
              <tr>
                <Th>
                  Channel name
                  <Text as="div" $type="extraSmall" $color="gray">
                    Network & link
                  </Text>
                </Th>
                <Th>Subscribers</Th>
                <Th>Notes</Th>
                <Th>
                  <SrOnly>Actions</SrOnly>
                </Th>
              </tr>
            </thead>
            <tbody>
              <TableContent
                colSpan={4}
                isLoading={isLoading}
                isError={isError}
                hasData={!!data?.length}
              >
                {data?.map((channel) => (
                  <TestingChannelsTableRow key={channel.id} channel={channel} />
                ))}
              </TableContent>
              <StyledFullWidthRow colSpan={4}>
                <AddButton onClick={() => setShowAddModal(true)}>
                  Add channel
                </AddButton>
              </StyledFullWidthRow>
            </tbody>
          </StyledTable>
        </ResponsiveTable>
      )}
      <AnimatePresence>
        {showAddModal && (
          <TestingChannelFormModal onClose={() => setShowAddModal(false)} />
        )}
      </AnimatePresence>
    </Box>
  );
};

export default TestingChannelsTable;
